import { DASH } from '../utils/displayValues';
import { DocumentTemplateShell, TemplateEmptyState } from './TemplateShared';
import {
  extractMarkupText,
  formatLongDate,
  type TemplatePatient,
} from './templateUtils';

export interface ProblemsTemplateRow {
  id: string;
  name?: string | null;
  onsetDate?: string;
  recordedDate?: string;
  clinicalStatus?: string;
  conditionNote?: string;
  dateUploaded?: string;
}

export type ProblemsTemplatePatient = TemplatePatient;

export interface ProblemsTemplateProps {
  problems: ProblemsTemplateRow[];
  patient?: ProblemsTemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightProblemId?: string;
}

const formatDate = (iso?: string) => formatLongDate(iso, '');

const tableHeaders = [
  'Problem',
  'Onset',
  'Recorded',
  'Clinical Status',
  'Notes',
] as const;

const problemTableGridClass = 'grid grid-cols-[1.25fr_1fr_1fr_1fr_2.25fr]';

export const ProblemsTemplate = ({
  problems,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightProblemId,
}: ProblemsTemplateProps) => {
  const rows = problems ?? [];
  const hasRows = rows.length > 0;

  return (
    <DocumentTemplateShell
      title="Problems Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        {!hasRows ? (
          <TemplateEmptyState message="No problems available" />
        ) : (
          <div className="overflow-hidden rounded-sm border border-zinc-200">
            <div
              data-testid="problems-table-header-row"
              className={`${problemTableGridClass} bg-slate-100`}
            >
              {tableHeaders.map((header, idx) => (
                <div
                  key={header}
                  className={`flex min-w-0 border-b border-zinc-300 px-3 py-3.5 ${
                    idx < tableHeaders.length - 1 ? 'border-r' : ''
                  }`}
                >
                  <p className="min-w-0 flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
                    {header}
                  </p>
                </div>
              ))}
            </div>

            {rows.map((problem, rowIdx) => {
              const isHighlighted =
                !!highlightProblemId && problem.id === highlightProblemId;
              const zebra = rowIdx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
              const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
              const cells = [
                problem.name,
                formatDate(problem.onsetDate),
                formatDate(problem.recordedDate),
                problem.clinicalStatus,
                extractMarkupText(problem.conditionNote),
              ];
              const rowKey =
                problem.id ?? `${problem.name ?? 'problem'}-${rowIdx}`;

              return (
                <div
                  key={rowKey}
                  id={`problem-${problem.id}`}
                  tabIndex={isHighlighted ? -1 : undefined}
                  data-testid={`problems-table-body-row-${rowKey}`}
                  className={`${problemTableGridClass} border-t border-zinc-200 ${rowBg}`}
                >
                  {cells.map((cell, colIdx) => (
                    <div
                      key={tableHeaders[colIdx]}
                      className={`flex min-w-0 px-3 py-3 ${
                        colIdx < cells.length - 1
                          ? 'border-r border-zinc-200'
                          : ''
                      }`}
                    >
                      <p
                        data-testid={
                          tableHeaders[colIdx] === 'Notes'
                            ? `problem-note-${rowKey}`
                            : undefined
                        }
                        className="min-w-0 flex-1 text-[10px] leading-tight break-words whitespace-pre-line text-zinc-950"
                      >
                        {cell?.toString().trim() || DASH}
                      </p>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </DocumentTemplateShell>
  );
};
